/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsArchive } from "../funcs/skillsArchive.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ArchiveSkillRequest,
  ArchiveSkillSecurity,
} from "../models/operations/archiveskill.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ArchiveSkillMutationVariables = {
  request: ArchiveSkillRequest;
  security?: ArchiveSkillSecurity | undefined;
  options?: RequestOptions;
};

export type ArchiveSkillMutationData = void;

export type ArchiveSkillMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * archive skills
 *
 * @remarks
 * Idempotently archive a skill. The implementation requires the skills product feature and skill write scope. Repeated requests for the same skill succeed without creating another state transition.
 */
export function useArchiveSkillMutation(
  options?: MutationHookOptions<
    ArchiveSkillMutationData,
    ArchiveSkillMutationError,
    ArchiveSkillMutationVariables
  >,
): UseMutationResult<
  ArchiveSkillMutationData,
  ArchiveSkillMutationError,
  ArchiveSkillMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildArchiveSkillMutation(client, options),
    ...options,
  });
}

export function mutationKeyArchiveSkill(): MutationKey {
  return ["@gram/client", "skills", "archive"];
}

export function buildArchiveSkillMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ArchiveSkillMutationVariables,
  ) => Promise<ArchiveSkillMutationData>;
} {
  return {
    mutationKey: mutationKeyArchiveSkill(),
    mutationFn: function archiveSkillMutationFn({
      request,
      security,
      options,
    }): Promise<ArchiveSkillMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsArchive(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
